/**
 * StepActionHeader
 *
 * Reusable per-step header that shows the step title on the left and a
 * complete/completed control on the right. It reuses the task wiring that was
 * previously in ClaimCoverageHeader to determine and update completion state.
 */
import { RefreshCw } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { useLorDocuments } from '@/components/claims-filing/lor/hooks/useLorData';
import { getLatestCoverageTaskByCanonicalType } from '@/components/claims-filing/premises/premisesFlow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api';
import {
  useAddLorToDocumentsMutation,
  useGetCoverageQuery,
  useGetCoveragesQuery,
  usePreparePremisesCertifiedMailMutation,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import {
  useGetMatterQuery,
  useSetLorEmailMetadataMutation,
} from '@/services/api/mattersService';
import {
  ContactHandleSchema,
  CoverageCategory,
  CoverageStatus,
  PropertyContact,
} from '@/services/types/coverage-types';
import type { LorDeliveryMethod } from '@/services/types/document-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

import { ClaimFilingStep } from '../hooks/useClaimFilingState';
import { DeliveryMethodPickerDialog } from './DeliveryMethodPickerDialog';
import { EmailDraftDialog } from './EmailDraftDialog';
import { buildLorDocumentSubtitle } from './lorDocUtils';
import { LorPickerDialog } from './LorPickerDialog';
import { PremisesReviewWizard } from './PremisesReviewWizard';

interface StepActionHeaderProps {
  step: ClaimFilingStep;
  matterId: string;
  coverageId: string;
}

export const StepActionHeader: React.FC<StepActionHeaderProps> = ({
  step,
  matterId,
  coverageId,
}) => {
  const { data: currentCoverage } = useGetCoverageQuery({
    matterId,
    coverageId,
  });

  const title = React.useMemo(() => {
    if (step === 'review') return 'Review Details';
    if (step === 'call') {
      return currentCoverage?.category === CoverageCategory.PROPERTY
        ? 'Open Premises Claim'
        : 'Open Claim';
    }
    if (step === 'lor') return 'Write a Letter of Representation';
    if (step === 'review_send') return 'Review & Send LOR';
    return 'Acknowledgement';
  }, [currentCoverage?.category, step]);

  const { data: tasksResponse, isLoading, refetch: refetchTasks } = useGetTasksQuery({
    matter: matterId,
    limit: 200,
  });
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation();
  const { data: taskStatuses = [] } = useGetTaskStatusesQuery();
  const [updateCoverage] = useUpdateCoverageMutation();
  const [addLorToDocuments, { isLoading: isAddingToDocuments }] =
    useAddLorToDocumentsMutation();
  const { lorDocs, isLoading: isLorDocsLoading } = useLorDocuments(matterId);
  const [isLorPickerOpen, setIsLorPickerOpen] = React.useState(false);
  const [isPremisesWizardOpen, setIsPremisesWizardOpen] = React.useState(false);
  const [pendingLorId, setPendingLorId] = React.useState<string | null>(null);
  const [isLorSubmitting, setIsLorSubmitting] = React.useState(false);
  const [isOtherMatterLorsOpen, setIsOtherMatterLorsOpen] =
    React.useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = React.useState(false);
  const [emailModalLorId, setEmailModalLorId] = React.useState<string | null>(
    null,
  );
  const [isSavingEmailMetadata, setIsSavingEmailMetadata] =
    React.useState(false);
  // Delivery method picker state (new flow between LOR picker and email modal)
  const [isDeliveryMethodPickerOpen, setIsDeliveryMethodPickerOpen] =
    React.useState(false);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
    React.useState<LorDeliveryMethod>('email');
  const [deliveryRecipients, setDeliveryRecipients] = React.useState<string[]>(
    [],
  );
  const [deliveryRecipientInput, setDeliveryRecipientInput] =
    React.useState('');
  // Store prefilled values separately so we can switch between them
  const [prefillEmailValue, setPrefillEmailValue] = React.useState('');
  const [prefillFaxValue, setPrefillFaxValue] = React.useState('');
  // Fax cover page fields
  const [faxCoverTo, setFaxCoverTo] = React.useState('');
  const [faxCoverFrom, setFaxCoverFrom] = React.useState('');
  const [selectedPremisesMailContactIdx, setSelectedPremisesMailContactIdx] =
    React.useState<number | null>(null);

  const { data: matter } = useGetMatterQuery(matterId, { skip: !matterId });
  const [setLorEmailMetadata] = useSetLorEmailMetadataMutation();
  const [preparePremisesCertifiedMail] = usePreparePremisesCertifiedMailMutation();

  const canonicalTypeForStep = React.useMemo(() => {
    return {
      review: CanonicalTaskType.REVIEW_FOUND_COVERAGE,
      call:
        currentCoverage?.category === 'PROPERTY'
          ? CanonicalTaskType.OPEN_PREMISES_CLAIM
          : CanonicalTaskType.OPEN_CLAIM,
      lor: CanonicalTaskType.SEND_LOR,
      review_send: CanonicalTaskType.SEND_LOR,
      ack: CanonicalTaskType.LOR_ACKNOWLEDGEMENT,
    } as const;
  }, [currentCoverage?.category]);

  const stepTask = React.useMemo(() => {
    const tasks = tasksResponse?.results || [];
    // For review step, use matter-level discovery task (Find 1P/3P Coverage)
    if (step === 'review') {
      let desiredType: CanonicalTaskType | undefined;
      if (currentCoverage?.category === 'FIRST_PARTY') {
        desiredType = 'Find 1P Coverage' as CanonicalTaskType;
      } else if (currentCoverage?.category === 'THIRD_PARTY') {
        desiredType = 'Find 3P Coverage' as CanonicalTaskType;
      }
      const discoveryCandidates = desiredType
        ? tasks.filter((t) => t.type?.canonical_type === desiredType)
        : [];
      if (discoveryCandidates.length > 0) {
        const completed = discoveryCandidates.find(
          (t) => t.status?.canonical_status === CanonicalTaskStatus.COMPLETED,
        );
        if (completed) return completed;
        const na = discoveryCandidates.find(
          (t) =>
            t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
        );
        if (na) return na;
        const actionable = discoveryCandidates.find(
          (t) =>
            t.status?.canonical_status !== CanonicalTaskStatus.NOT_APPLICABLE,
        );
        return actionable || discoveryCandidates[0];
      }
      // Backwards compatibility: fall back to coverage-linked REVIEW_FOUND_COVERAGE task
      const reviewCandidates = tasks.filter(
        (t) =>
          t.coverage_id === coverageId &&
          t.type?.canonical_type === CanonicalTaskType.REVIEW_FOUND_COVERAGE,
      );
      if (reviewCandidates.length === 0) return undefined;
      const reviewCompleted = reviewCandidates.find(
        (t) => t.status?.canonical_status === CanonicalTaskStatus.COMPLETED,
      );
      if (reviewCompleted) return reviewCompleted;
      const reviewNA = reviewCandidates.find(
        (t) =>
          t.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
      );
      if (reviewNA) return reviewNA;
      const reviewActionable = reviewCandidates.find(
        (t) =>
          t.status?.canonical_status !== CanonicalTaskStatus.NOT_APPLICABLE,
      );
      return reviewActionable || reviewCandidates[0];
    }
    // For call/lor/ack steps, keep coverage-linked task lookup
    return getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageId,
      canonicalTypeForStep[step],
    );
  }, [
    tasksResponse,
    step,
    coverageId,
    canonicalTypeForStep,
    currentCoverage?.category,
  ]);

  // For the "lor" (Write LOR) step, consider it complete when the task is
  // under review OR completed. The "write" phase is done once sent for review.
  const isCompleted = React.useMemo(() => {
    if (!stepTask) return false;
    const taskCompleted =
      stepTask.status?.canonical_status === CanonicalTaskStatus.COMPLETED;
    if (step === 'lor') {
      const taskUnderReview =
        stepTask.status?.name?.toLowerCase() === 'under review' ||
        stepTask.status?.canonical_status === CanonicalTaskStatus.BLOCKED;
      return taskCompleted || taskUnderReview;
    }
    return taskCompleted;
  }, [stepTask, step]);

  const isNA =
    !!stepTask &&
    stepTask.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE;

  const completedDate = React.useMemo(() => {
    if (!stepTask?.completed_at) return null;
    const d = new Date(stepTask.completed_at);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }, [stepTask?.completed_at]);

  const underReviewStatusId = React.useMemo(() => {
    const explicit = taskStatuses.find(
      (s) =>
        s.name?.toLowerCase() === 'under review' ||
        (s.canonical_status === CanonicalTaskStatus.BLOCKED &&
          s.name?.toLowerCase().includes('review')),
    );
    if (explicit) return explicit.id;
    const blocked = taskStatuses.find(
      (s) => s.canonical_status === CanonicalTaskStatus.BLOCKED,
    );
    return blocked?.id;
  }, [taskStatuses]);

  const completedStatusId = React.useMemo(() => {
    const completed = taskStatuses.find(
      (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
    );
    return completed?.id;
  }, [taskStatuses]);

  const isUnderReview = React.useMemo(() => {
    if (!stepTask?.status) return false;
    if (stepTask.status.name?.toLowerCase() === 'under review') {
      return true;
    }
    if (!underReviewStatusId) return false;
    return stepTask.status.id === underReviewStatusId;
  }, [stepTask, underReviewStatusId]);

  const sendTaskToUnderReview = React.useCallback(async () => {
    if (!stepTask || !underReviewStatusId) {
      throw new Error('Unable to locate task for this step.');
    }

    await updateTask({
      matterId,
      taskId: stepTask.id,
      task: {
        status_id: underReviewStatusId,
        completed_at: undefined,
      },
    }).unwrap();

    if (step === 'review') {
      await updateCoverage({
        matterId,
        coverageId,
        coverage: { status: CoverageStatus.APPROVED },
      }).unwrap();
    }
  }, [
    coverageId,
    matterId,
    step,
    stepTask,
    underReviewStatusId,
    updateCoverage,
    updateTask,
  ]);

  const primaryClientName = React.useMemo(() => {
    const client = matter?.clients?.[0];
    if (client) {
      const parts = [client.first_name, client.last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
      if (parts) {
        return parts;
      }
      if (client.name) {
        return client.name;
      }
    }
    return matter?.name || 'our client';
  }, [matter]);

  // Check if this is a premises coverage (PROPERTY category)
  const isPremisesCoverage =
    currentCoverage?.category === CoverageCategory.PROPERTY;
  const { data: matterCoverages = [] } = useGetCoveragesQuery(
    { matterId },
    { skip: !isPremisesCoverage || !matterId },
  );

  // Get property contacts from coverage for premises cases
  const propertyContacts = React.useMemo(() => {
    if (!isPremisesCoverage) return [];
    return (currentCoverage?.property_contacts ?? []) as PropertyContact[];
  }, [isPremisesCoverage, currentCoverage?.property_contacts]);

  const coveragesById = React.useMemo(
    () => new Map(matterCoverages.map((coverage) => [coverage.id, coverage])),
    [matterCoverages],
  );

  const currentAttachedLorId = currentCoverage?.lor_document_id ?? null;

  const currentCoverageLorDocs = React.useMemo(() => {
    const matchingDocs = lorDocs.filter(
      (doc) =>
        doc.coverage_id === coverageId || doc.id === currentAttachedLorId,
    );
    return [...matchingDocs].sort((left, right) => {
      if (left.id === currentAttachedLorId) return -1;
      if (right.id === currentAttachedLorId) return 1;
      return (
        new Date(right.created_at).getTime() - new Date(left.created_at).getTime()
      );
    });
  }, [coverageId, currentAttachedLorId, lorDocs]);

  const otherMatterLorDocs = React.useMemo(() => {
    const currentDocIds = new Set(currentCoverageLorDocs.map((doc) => doc.id));
    return lorDocs
      .filter((doc) => !currentDocIds.has(doc.id))
      .sort(
        (left, right) =>
          new Date(right.created_at).getTime() -
          new Date(left.created_at).getTime(),
      );
  }, [currentCoverageLorDocs, lorDocs]);

  const handleCancelEmailModal = React.useCallback(() => {
    setIsEmailModalOpen(false);
    setEmailModalLorId(null);
  }, []);

  const finalizeSend = React.useCallback(
    async (
      lorDocumentId: string,
      premisesMailContactIdx: number | null = null,
    ) => {
      try {
        setIsLorSubmitting(true);
        await updateCoverage({
          matterId,
          coverageId,
          coverage: { lor_document_id: lorDocumentId },
        }).unwrap();
        if (!stepTask?.id) {
          throw new Error('Unable to locate the active Send LOR task.');
        }
        if (isPremisesCoverage && premisesMailContactIdx !== null) {
          const selectedPropertyContact =
            propertyContacts[premisesMailContactIdx] ?? null;
          if (!selectedPropertyContact?.id) {
            throw new Error('Selected property contact is missing an id.');
          }
          await preparePremisesCertifiedMail({
            matterId,
            coverageId,
            taskId: stepTask.id,
            createNewAttempt: true,
            lorDocumentId,
            contactId: selectedPropertyContact.id,
            contactIndex: premisesMailContactIdx,
          }).unwrap();
        }
        await sendTaskToUnderReview();
        toast.success(
          isPremisesCoverage
            ? 'LoR attached, certified mail package prepared, and submitted for internal review'
            : 'LoR attached and submitted for internal review',
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        toast.error('Failed to submit LoR for internal review');
        throw error;
      } finally {
        setIsLorSubmitting(false);
      }
    },
    [
      coverageId,
      isPremisesCoverage,
      matterId,
      propertyContacts,
      preparePremisesCertifiedMail,
      sendTaskToUnderReview,
      stepTask?.id,
      updateCoverage,
    ],
  );

  const handleMarkComplete = async () => {
    if (!stepTask || !completedStatusId) {
      return;
    }
    try {
      await updateTask({
        matterId,
        taskId: stepTask.id,
        task: {
          status_id: completedStatusId,
          completed_at: new Date().toISOString(),
        },
      }).unwrap();
      toast.success('Task marked as complete');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      toast.error('Failed to mark as complete');
    }
  };

  const defaultLorSelection = React.useMemo(() => {
    if (currentCoverage?.lor_document_id) {
      return currentCoverage.lor_document_id;
    }
    if (isPremisesCoverage && currentCoverageLorDocs.length > 0) {
      return currentCoverageLorDocs[0]?.id ?? null;
    }
    return lorDocs[0]?.id ?? null;
  }, [
    currentCoverage?.lor_document_id,
    currentCoverageLorDocs,
    isPremisesCoverage,
    lorDocs,
  ]);

  const handleLorSendForReview = () => {
    if (isLorSubmitting) return;
    if (!underReviewStatusId) return;
    if (!lorDocs.length) {
      toast.error(
        'Generate a Letter of Representation before submitting it for internal review.',
      );
      return;
    }
    if (isPremisesCoverage) {
      setIsPremisesWizardOpen(true);
      return;
    }
    setPendingLorId(defaultLorSelection);
    setIsOtherMatterLorsOpen(false);
    setIsLorPickerOpen(true);
  };

  // After LOR is selected, open delivery method picker
  const handleAttachLorAndSend = React.useCallback(() => {
    if (isLorSubmitting) {
      return;
    }
    if (!pendingLorId) {
      toast.error('Select a Letter of Representation to continue.');
      return;
    }

    if (isPremisesCoverage) {
      const firstMailableContactIdx = propertyContacts.findIndex((contact) =>
        Boolean(contact.mailing_address),
      );
      setSelectedPremisesMailContactIdx(
        firstMailableContactIdx >= 0 ? firstMailableContactIdx : null,
      );
      setIsLorPickerOpen(false);
      setIsDeliveryMethodPickerOpen(true);
      return;
    }

    // Standard flow for non-premises coverages
    // Pre-fill recipient with adjuster's email/fax from coverage handles
    // Adjuster handles
    const adjusters = currentCoverage?.adjusters as
      | { handles?: ContactHandleSchema[] }[]
      | undefined;
    const adjusterHandles = adjusters?.[0]?.handles ?? [];
    const adjusterEmail =
      adjusterHandles.find((h) =>
        (h.handle_type || '').toLowerCase().includes('email'),
      )?.value ?? '';
    const adjusterFax =
      adjusterHandles.find((h) =>
        (h.handle_type || '').toLowerCase().includes('fax'),
      )?.value ?? '';

    // Carrier handles as fallback
    const carrierHandles = currentCoverage?.carrier?.handles ?? [];
    const carrierEmail =
      carrierHandles.find((h: ContactHandleSchema) =>
        (h.handle_type || '').toLowerCase().includes('email'),
      )?.value ?? '';
    const carrierFax =
      carrierHandles.find((h: ContactHandleSchema) =>
        (h.handle_type || '').toLowerCase().includes('fax'),
      )?.value ?? '';

    // Prefer adjuster info, fall back to carrier
    const emailValue = adjusterEmail || carrierEmail;
    const faxValue = adjusterFax || carrierFax;

    // Store both values so we can switch between them
    setPrefillEmailValue(emailValue);
    setPrefillFaxValue(faxValue);

    // Default to email if available, otherwise fax
    const defaultMethod: LorDeliveryMethod = emailValue ? 'email' : 'fax';
    const initialRecipient = defaultMethod === 'email' ? emailValue : faxValue;

    setDeliveryRecipients(initialRecipient ? [initialRecipient] : []);
    setDeliveryRecipientInput('');
    setSelectedDeliveryMethod(defaultMethod);
    setIsLorPickerOpen(false);
    setIsDeliveryMethodPickerOpen(true);
  }, [
    pendingLorId,
    currentCoverage?.adjusters,
    currentCoverage?.carrier?.handles,
    isPremisesCoverage,
    isLorSubmitting,
    propertyContacts,
  ]);

  // When delivery method changes, update the prefilled recipient
  const handleDeliveryMethodChange = React.useCallback(
    (method: LorDeliveryMethod) => {
      setSelectedDeliveryMethod(method);
      // Clear current recipients and prefill with the appropriate value
      const newRecipient =
        method === 'email' ? prefillEmailValue : prefillFaxValue;
      setDeliveryRecipients(newRecipient ? [newRecipient] : []);
      setDeliveryRecipientInput('');

      // Autofill fax cover page fields when fax is selected
      if (method === 'fax') {
        // To: "Claim - {claim number}" if available
        const claimNumber = currentCoverage?.claims?.[0]?.claim_identifier;
        setFaxCoverTo(claimNumber ? `Claim - ${claimNumber}` : '');

        // From: firm name
        const firmName = matter?.firm?.full_name || matter?.firm?.name || '';
        setFaxCoverFrom(firmName);
      }
    },
    [prefillEmailValue, prefillFaxValue, currentCoverage?.claims, matter?.firm],
  );

  // Validation helpers for email/phone
  const isValidEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const isValidFaxNumber = (number: string): boolean => {
    const digits = number.replace(/\D/g, '');
    return digits.length >= 10;
  };

  // Add recipient to the list
  const addDeliveryRecipient = React.useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;

      if (selectedDeliveryMethod === 'email') {
        if (isValidEmail(trimmed) && !deliveryRecipients.includes(trimmed)) {
          setDeliveryRecipients((prev) => [...prev, trimmed]);
          setDeliveryRecipientInput('');
        } else if (!isValidEmail(trimmed)) {
          toast.error('Please enter a valid email address');
        }
      } else if (
        isValidFaxNumber(trimmed) &&
        !deliveryRecipients.includes(trimmed)
      ) {
        setDeliveryRecipients((prev) => [...prev, trimmed]);
        setDeliveryRecipientInput('');
      } else if (!isValidFaxNumber(trimmed)) {
        toast.error('Please enter a valid fax number (at least 10 digits)');
      }
    },
    [deliveryRecipients, selectedDeliveryMethod],
  );

  // Remove recipient from list
  const removeDeliveryRecipient = React.useCallback((value: string) => {
    setDeliveryRecipients((prev) => prev.filter((r) => r !== value));
  }, []);

  // Handle keyboard input for recipient chips
  const handleRecipientKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
        e.preventDefault();
        addDeliveryRecipient(deliveryRecipientInput);
      } else if (
        e.key === 'Backspace' &&
        deliveryRecipientInput === '' &&
        deliveryRecipients.length > 0
      ) {
        setDeliveryRecipients((prev) => prev.slice(0, -1));
      }
    },
    [addDeliveryRecipient, deliveryRecipientInput, deliveryRecipients.length],
  );

  // After delivery method is selected, proceed to email/fax composition
  const handleDeliveryMethodSelected = React.useCallback(async () => {
    if (isLorSubmitting) {
      return;
    }
    if (isPremisesCoverage) {
      if (!pendingLorId) {
        toast.error('Select a Letter of Representation to continue.');
        return;
      }
      if (selectedPremisesMailContactIdx === null) {
        toast.error('Select a property contact with a mailing address.');
        return;
      }
      const selectedContact = propertyContacts[selectedPremisesMailContactIdx];
      if (!selectedContact?.mailing_address) {
        toast.error('The selected property contact needs a mailing address.');
        return;
      }
      try {
        await finalizeSend(pendingLorId, selectedPremisesMailContactIdx);
      } catch {
        return;
      }
      setIsDeliveryMethodPickerOpen(false);
      setPendingLorId(null);
      return;
    }

    // Standard flow for non-premises cases
    // Commit any pending input
    if (deliveryRecipientInput.trim()) {
      addDeliveryRecipient(deliveryRecipientInput);
    }

    if (deliveryRecipients.length === 0 && !deliveryRecipientInput.trim()) {
      toast.error(
        selectedDeliveryMethod === 'email'
          ? 'Enter at least one email address.'
          : 'Enter at least one fax number.',
      );
      return;
    }

    // Use either the existing recipients or the pending input
    const finalRecipients =
      deliveryRecipients.length > 0
        ? deliveryRecipients
        : [deliveryRecipientInput.trim()];

    setEmailModalLorId(pendingLorId);
    setIsDeliveryMethodPickerOpen(false);
    setIsEmailModalOpen(true);

    // Update deliveryRecipients state with final value for saving
    setDeliveryRecipients(finalRecipients);
  }, [
    addDeliveryRecipient,
    deliveryRecipientInput,
    deliveryRecipients,
    finalizeSend,
    isPremisesCoverage,
    isLorSubmitting,
    pendingLorId,
    propertyContacts,
    selectedDeliveryMethod,
    selectedPremisesMailContactIdx,
  ]);

  const renderLorOptionCard = React.useCallback(
    (
      doc: (typeof lorDocs)[number],
      options?: {
        highlightCurrentCoverage?: boolean;
      },
    ) => {
      const label =
        doc.document_template?.name || 'Letter of Representation';
      const created = new Date(doc.created_at).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
      const inputId = `lor-doc-${doc.id}`;
      const matchingCoverage =
        (doc.coverage_id ? coveragesById.get(doc.coverage_id) : null) ??
        (doc.id === currentAttachedLorId ? currentCoverage : null);
      const subtitle = buildLorDocumentSubtitle(doc, matchingCoverage);
      const isAttachedToCoverage = doc.id === currentAttachedLorId;
      const isCurrentCoverageDoc =
        options?.highlightCurrentCoverage ||
        doc.coverage_id === coverageId ||
        isAttachedToCoverage;
      let cardClassName = 'border-zinc-200 bg-white';
      if (isAttachedToCoverage) {
        cardClassName = 'border-emerald-200 bg-emerald-50/60';
      } else if (isCurrentCoverageDoc) {
        cardClassName = 'border-violet-200 bg-violet-50/40';
      }

      return (
        <div
          key={doc.id}
          className={`flex items-start gap-3 rounded-xl border p-4 transition ${cardClassName}`}
        >
          <RadioGroupItem
            id={inputId}
            value={doc.id}
            disabled={isLorSubmitting}
            className="mt-1"
          />
          <label htmlFor={inputId} className="flex-1 cursor-pointer space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm font-semibold text-zinc-800">{label}</div>
              {isAttachedToCoverage ? (
                <Badge
                  variant="outline"
                  className="border-emerald-200 bg-emerald-50 text-emerald-700"
                >
                  Attached to this coverage
                </Badge>
              ) : null}
              {isCurrentCoverageDoc && !isAttachedToCoverage ? (
                <Badge
                  variant="outline"
                  className="border-violet-200 bg-violet-50 text-violet-700"
                >
                  This coverage
                </Badge>
              ) : null}
            </div>
            <div className="text-xs text-zinc-500">{created}</div>
            <div className="text-xs text-zinc-600">{subtitle}</div>
          </label>
        </div>
      );
    },
    [
      coverageId,
      coveragesById,
      currentAttachedLorId,
      currentCoverage,
      isLorSubmitting,
    ],
  );

  const renderLorPickerList = React.useCallback(() => {
    if (isLorDocsLoading) {
      return (
        <div className="px-2 py-4 text-sm text-zinc-500">Loading documents…</div>
      );
    }

    if (lorDocs.length === 0) {
      return (
        <div className="px-2 py-4 text-sm text-zinc-500">
          No Letters of Representation have been generated for this matter.
        </div>
      );
    }

    return (
      <RadioGroup
        value={pendingLorId ?? ''}
        onValueChange={(val) => setPendingLorId(val)}
        className="space-y-2"
      >
        {lorDocs.map((doc) => renderLorOptionCard(doc))}
      </RadioGroup>
    );
  }, [
    isLorDocsLoading,
    lorDocs,
    pendingLorId,
    renderLorOptionCard,
  ]);

  const renderPrimaryAction = () => {
    if (isLoading) {
      return <div className="h-9 w-36 bg-zinc-100 rounded animate-pulse" />;
    }
    if (!stepTask) {
      const coverageNeedsApproval =
        currentCoverage &&
        currentCoverage.status !== CoverageStatus.APPROVED;
      if (coverageNeedsApproval) {
        return (
          <div className="flex items-center gap-3">
            <div className="text-xs text-amber-600">
              Approve this coverage to generate tasks
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-3 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={async () => {
                try {
                  await updateCoverage({
                    matterId,
                    coverageId,
                    coverage: { status: CoverageStatus.APPROVED },
                  }).unwrap();
                  toast.success('Coverage approved — tasks will generate shortly');
                } catch {
                  toast.error('Failed to approve coverage');
                }
              }}
            >
              Approve Coverage
            </Button>
          </div>
        );
      }
      return (
        <button
          type="button"
          className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
          onClick={() => refetchTasks()}
        >
          Unable to find task
          <RefreshCw className="h-3 w-3" />
        </button>
      );
    }
    if (isCompleted) {
      // For "lor" step, show internal review state rather than "Completed"
      if (
        step === 'lor' &&
        stepTask?.status?.canonical_status !== CanonicalTaskStatus.COMPLETED
      ) {
        return (
          <div className="flex items-center gap-3">
            <div className="text-sm text-emerald-600">
              Submitted for internal review
            </div>
            <Button
              size="lg"
              variant="outline"
              className="h-9 px-4"
              onClick={handleLorSendForReview}
              disabled={
                isUpdating || !underReviewStatusId || lorDocs.length === 0
              }
            >
              {isUpdating ? 'Updating…' : 'Replace Draft'}
            </Button>
          </div>
        );
      }
      return (
        <div className="text-sm text-zinc-600">Completed {completedDate}</div>
      );
    }
    if (isNA) {
      return <div className="text-sm text-green-700">Marked NA</div>;
    }

    switch (step) {
      case 'lor':
        // Note: Under review state is handled in the isCompleted block above
        return (
            <Button
              size="lg"
              className="h-9 px-4"
              onClick={handleLorSendForReview}
              disabled={
                isUpdating || isLorSubmitting || !underReviewStatusId || lorDocs.length === 0
              }
            >
              {isLorSubmitting
                ? 'Preparing certified mail…'
                : isUpdating
                  ? 'Submitting…'
                  : 'Submit for Internal Review'}
            </Button>
        );

      case 'review_send':
        if (isUnderReview) {
          return <div className="text-sm text-blue-600">Under internal review</div>;
        }
        if (isPremisesCoverage) {
          return (
            <div className="text-sm text-zinc-600">
              This step completes automatically after correspondence marks the mail as sent.
            </div>
          );
        }
        return (
          <div className="flex items-center gap-3">
            <Button
              size="lg"
              variant="outline"
              className="h-9 px-4"
              onClick={async () => {
                try {
                  const result = await addLorToDocuments({
                    matterId,
                    coverageId,
                  }).unwrap();
                  if (result.status === 'already_exists') {
                    toast.info('LOR already exists in documents');
                  } else {
                    toast.success('LOR added to documents');
                  }
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error(err);
                  toast.error('Failed to add LOR to documents');
                }
              }}
              disabled={isAddingToDocuments}
            >
              {isAddingToDocuments ? 'Adding…' : 'Add to Documents'}
            </Button>
            <Button
              size="lg"
              className="h-9 px-4"
              onClick={handleMarkComplete}
              disabled={isUpdating || !completedStatusId}
            >
              {isUpdating ? 'Updating…' : 'Mark Complete'}
            </Button>
          </div>
        );

      default:
        // All other steps (Review, Call, Ack) use Mark Complete
        if (isUnderReview) {
          return <div className="text-sm text-blue-600">Under internal review</div>;
        }
        return (
          <Button
            size="lg"
            className="h-9 px-4"
            onClick={handleMarkComplete}
            disabled={isUpdating || !completedStatusId}
          >
            {isUpdating ? 'Updating…' : 'Mark Complete'}
          </Button>
        );
    }
  };

  return (
    <div className="w-full">
      <div className="mx-auto max-w-7xl px-4 py-4 flex items-center justify-between">
        <h2 className="text-3xl font-serif text-zinc-800 m-0">{title}</h2>
        <div className="flex items-center gap-3">{renderPrimaryAction()}</div>
      </div>
      {/* Premises wizard dialog */}
      {isPremisesCoverage && (
        <PremisesReviewWizard
          open={isPremisesWizardOpen}
          onOpenChange={setIsPremisesWizardOpen}
          lorDocs={lorDocs}
          currentCoverageLorDocs={currentCoverageLorDocs}
          otherMatterLorDocs={otherMatterLorDocs}
          propertyContacts={propertyContacts}
          isSubmitting={isLorSubmitting}
          defaultLorSelection={defaultLorSelection}
          renderLorOptionCard={renderLorOptionCard}
          onSubmit={async (lorId, contactIdx) => {
            await finalizeSend(lorId, contactIdx);
            setIsPremisesWizardOpen(false);
          }}
        />
      )}

      {/* Standard (non-premises) LOR picker dialog */}
      {!isPremisesCoverage && (
        <LorPickerDialog
          open={isLorPickerOpen}
          onOpenChange={(next) => {
            setIsLorPickerOpen(next);
            if (!next) {
              setPendingLorId(null);
              setIsOtherMatterLorsOpen(false);
            }
          }}
          isSubmitting={isLorSubmitting}
          hasPendingSelection={Boolean(pendingLorId)}
          onContinue={handleAttachLorAndSend}
        >
          {renderLorPickerList()}
        </LorPickerDialog>
      )}

      {/* Delivery Method Picker Modal */}
      <DeliveryMethodPickerDialog
        open={isDeliveryMethodPickerOpen}
        onOpenChange={(next) => {
          setIsDeliveryMethodPickerOpen(next);
          if (!next) {
            setDeliveryRecipients([]);
            setDeliveryRecipientInput('');
            setFaxCoverTo('');
            setFaxCoverFrom('');
            setSelectedPremisesMailContactIdx(null);
          }
        }}
        isPremisesCoverage={isPremisesCoverage}
        isSubmitting={isLorSubmitting}
        propertyContacts={propertyContacts}
        selectedPremisesMailContactIdx={selectedPremisesMailContactIdx}
        onSelectPremisesContact={setSelectedPremisesMailContactIdx}
        selectedDeliveryMethod={selectedDeliveryMethod}
        onDeliveryMethodChange={handleDeliveryMethodChange}
        deliveryRecipients={deliveryRecipients}
        deliveryRecipientInput={deliveryRecipientInput}
        onDeliveryRecipientInputChange={setDeliveryRecipientInput}
        onAddRecipient={addDeliveryRecipient}
        onRemoveRecipient={removeDeliveryRecipient}
        onRecipientKeyDown={handleRecipientKeyDown}
        faxCoverTo={faxCoverTo}
        onFaxCoverToChange={setFaxCoverTo}
        faxCoverFrom={faxCoverFrom}
        onFaxCoverFromChange={setFaxCoverFrom}
        onBack={() => {
          setIsDeliveryMethodPickerOpen(false);
          setIsLorPickerOpen(true);
        }}
        onContinue={handleDeliveryMethodSelected}
      />

      <EmailDraftDialog
        open={isEmailModalOpen}
        onClose={handleCancelEmailModal}
        onSubmit={async (data) => {
          if (!emailModalLorId) return;
          setIsSavingEmailMetadata(true);
          try {
            await setLorEmailMetadata({
              matterId,
              documentId: emailModalLorId,
              payload: {
                email_body: data.body,
                attachments:
                  data.policeReportId && data.policeReportFilename
                    ? [
                        {
                          blob_id: data.policeReportId,
                          filename: data.policeReportFilename,
                        },
                      ]
                    : [],
                delivery_method: selectedDeliveryMethod,
                delivery_recipient: deliveryRecipients.join(', '),
                ...(selectedDeliveryMethod === 'fax' && {
                  fax_cover_to: faxCoverTo.trim() || undefined,
                  fax_cover_from: faxCoverFrom.trim() || undefined,
                }),
              },
            }).unwrap();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
            toast.error('Failed to save email details');
            return;
          } finally {
            setIsSavingEmailMetadata(false);
          }
          try {
            await finalizeSend(emailModalLorId);
          } catch {
            return;
          }
          setIsEmailModalOpen(false);
          setEmailModalLorId(null);
          setPendingLorId(null);
        }}
        deliveryMethod={selectedDeliveryMethod}
        primaryClientName={primaryClientName}
        matterId={matterId}
        isSubmitting={isLorSubmitting || isSavingEmailMetadata}
      />
    </div>
  );
};
